import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavController } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import _ from 'lodash';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { v4 as uuidv4 } from 'uuid';

import { AutoresponderComponent } from 'src/app/components/autoresponder/autoresponder.component';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { SupportStepsModule } from 'src/app/components/support-steps/support-steps.module';
import { WorkingHoursComponent } from 'src/app/components/working-hours/working-hours.component';
import { WorkingHoursModule } from 'src/app/components/working-hours/working-hours.module';
import { DEFAULT_INITIAL_SUPPORT_STEP, EXTERNAL_CHAT_CONNECTION_TYPES, FEATURES, routes } from 'src/app/constants';
import { removeUndefinedFields } from 'src/app/utils/utils';
import { environment } from 'src/environments/environment';
import { ExternalChatConnection, Profile } from 'src/models';
import { Project } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { ExternalChatConnectionsService } from 'src/services/external-chat-connections.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { UsersService } from 'src/services/users.service';

@Component({
  selector: 'app-edit-ms-teams-channel',
  templateUrl: './edit-ms-teams-channel.component.html',
  styleUrls: ['./edit-ms-teams-channel.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    SharedPipesModule,
    SupportStepsModule,
    NgxTippyModule,
    WorkingHoursModule,
    ChannelSelectorModule,
    AutoresponderComponent,
    RouterModule,
  ],
})
export class EditMsTeamsChannelComponent implements OnInit {
  forwardEmail: string;
  connection: Partial<ExternalChatConnection> | null;
  featureEnabled = true;
  newAllowlistTeam = '';
  newAllowlistChannel = '';
  connectedUsers: Partial<Profile>[] = [];
  customerId: string;
  isLoading = false;
  project?: Partial<Project>;
  hideProject?: boolean;

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private externalChatConnectionsService: ExternalChatConnectionsService,
    private usersService: UsersService,
    private navController: NavController,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async ngOnInit() {
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.EXTERNAL_CHAT_CONNECTIONS);

    if (this.customerId) {
      this.getMicrosoftTeamsConnection();
    } else {
      if (this.authService.selectedProject) {
        this.project = this.authService.selectedProject;
        this.hideProject = true;
      } else {
        const projectId = this.route.snapshot.params['projectId'] ?? this.authService.tenant.defaultProjectId;
        this.project = this.authService.projects.find((p) => p.id === projectId) as Project;
        this.hideProject = this.authService.projects.length === 1;
      }

      this.connection = {
        supportSteps: [DEFAULT_INITIAL_SUPPORT_STEP],
        name: 'Microsoft Teams',
        type: EXTERNAL_CHAT_CONNECTION_TYPES.MICROSOFT_TEAMS,
        channelId: '',
        enabled: true,
        projectId: this.project?.id,
      };
    }
  }

  async getMicrosoftTeamsConnection() {
    const result = await this.externalChatConnectionsService.list({
      order: ['createdAt'],
      descending: false,
      where: [
        {
          field: 'customerId',
          operator: '==',
          value: this.customerId,
        },
      ],
    });

    if (result.data.length) {
      this.connection = result.data[0];
      await this.getConnectedUsers();
    } else {
      this.msgSrvc.showError('No Microsoft Teams connection found');
    }
  }

  async getConnectedUsers() {
    const connectedUserIds = this.connection?.providerConfig?.connectedUserIds ?? [];
    const users = connectedUserIds.length
      ? await this.usersService.list({
          where: [{ field: 'id', operator: 'in', value: connectedUserIds }],
        })
      : { data: [] };

    this.connectedUsers = users.data ?? [];
  }

  isAlreadyConnected() {
    return this.connectedUsers.some((user) => user.id === this.authService.userId);
  }

  async addConnectedUser() {
    await this.microsoftTeamsOauth(this.connection.oauthId);
    await this.externalChatConnectionsService.addMicrosoftTeamsConnection(this.connection.id);

    this.connection.providerConfig.connectedUserIds.push(this.authService.userId);

    await this.getMicrosoftTeamsConnection();
  }

  async editWorkingHours() {
    const modal = await this.modalCtrl.create({
      component: WorkingHoursComponent,
      componentProps: {
        originalWorkingHours: this.authService.tenant?.workingHours ?? {},
        onSave: async (updates: any) => this.authService.updateTenant(updates),
      },
    });

    modal.present();
  }

  async save() {
    try {
      this.isLoading = true;
      const payload: Partial<ExternalChatConnection> = removeUndefinedFields(this.connection);

      if (!payload?.id) {
        const oauthId = await this.microsoftTeamsOauth();
        const connection = await this.externalChatConnectionsService.create({
          ...payload,
          oauthId,
        });

        await this.externalChatConnectionsService.addMicrosoftTeamsConnection(connection.id);
      } else {
        await this.externalChatConnectionsService.update(this.connection.id, this.connection);
      }

      this.getMicrosoftTeamsConnection();

      this.msgSrvc.show('✔︎ Saved');

      this.dismiss();
    } catch (e) {
      this.msgSrvc.showError(e);
    } finally {
      this.isLoading = false;
    }
  }

  supportStepsDidChange(supportSteps: any[]) {
    this.connection.supportSteps = supportSteps;
  }

  async dismiss() {
    await this.modalCtrl.dismiss();
  }

  async delete() {
    if (!this.connection.id) {
      this.connection = null;
      return;
    }

    const alert = await this.alertCtrl.create({
      header: 'Disable Microsoft Teams connection',
      subHeader: 'Are you sure you want to disable this Microsoft Teams connection?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: async () => {
            if (this.connection.id) {
              try {
                this.isLoading = true;

                await this.externalChatConnectionsService.delete(this.connection.id);

                this.dismiss();

                this.navController.navigateRoot(
                  `${routes.DASHBOARD}/${routes.CHANNELS}/${EXTERNAL_CHAT_CONNECTION_TYPES.MICROSOFT_TEAMS}`,
                );
              } catch (e) {
                this.msgSrvc.showError(e);
              } finally {
                this.isLoading = false;
              }
            }
          },
        },
      ],
    });

    await alert.present();
  }

  showMicrosoftTeamsHelpdesk() {
    window.open('https://docs.unthread.io/integrations/microsoft-teams', '_blank');
  }

  viewInbox(connection: ExternalChatConnection) {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${connection.type}/${connection.id}`);
  }

  onConnectionChannelChanged(slackChannel) {
    this.connection.channelId = slackChannel?.id ?? null;
    this.connection.slackChannel = slackChannel;

    if (this.connection.id) {
      this.save();
    }
  }

  autoresponderDidChange(autoresponder: any) {
    this.connection.autoresponder = autoresponder;
  }

  async microsoftTeamsOauth(oauthId = uuidv4()) {
    const Nango = (await import('@nangohq/frontend')).default;
    const nango = new Nango({ publicKey: environment.nangoPublicKey });
    const { hmac } = (await this.apiService.postPromise(`/integrations/pre-register`, {
      integration: EXTERNAL_CHAT_CONNECTION_TYPES.MICROSOFT_TEAMS,
      connectionParams: [oauthId, this.authService.userId],
    })) as any;

    const options = {
      hmac,
      params: {},
      authorization_params: { prompt: '' },
    };

    try {
      await nango.auth(
        EXTERNAL_CHAT_CONNECTION_TYPES.MICROSOFT_TEAMS,
        `${this.authService.tenant.id}-${oauthId}-${this.authService.userId}`,
        options,
      );
    } catch (err: any) {
      throw new Error(err.message);
    }

    return oauthId;
  }

  addToAllowlist() {
    if (this.newAllowlistTeam && this.newAllowlistChannel) {
      if (!this.connection.channelAllowlist) {
        this.connection.channelAllowlist = [];
      }

      this.connection.channelAllowlist.push(`${this.newAllowlistTeam.trim()}:${this.newAllowlistChannel.trim()}`);

      this.newAllowlistTeam = '';
      this.newAllowlistChannel = '';
    }
  }

  removeFromAllowlist(index: number) {
    this.connection.channelAllowlist.splice(index, 1);
  }

  trackById(user: Profile) {
    return user.id;
  }

  selectProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: false,
      selectedProjectId: this.project?.id,
      callback: async (_id: string, _name: string, object: any) => {
        this.project = object;
      },
    });
  }
}
